import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  CheckCircle
} from 'lucide-react'
import { features, stats } from '../lib/data'

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - DevSecRoot</title>
        <meta name="description" content="Learn about DevSecRoot's mission to deliver secure development solutions with cutting-edge technology and proven methodologies." />
        <link rel="canonical" href="https://devsecroot.com/about" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              About DevSecRoot
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              DevSecRoot unifies software engineering and security from day zero. We design scalable systems, 
              threat-model their risks, and automate defenses—so reliability, speed, and trust share the same root.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-dsr-text/70 mb-6">
                To empower organizations with secure, scalable, and innovative technology solutions 
                that protect their assets while accelerating their digital transformation.
              </p>
              <p className="text-lg text-dsr-text/70 mb-6">
                We believe that security should be built into every application from the ground up, 
                not bolted on as an afterthought. This philosophy drives everything we do.
              </p>
              <Link to="/contact" className="btn-primary">
                Get Started
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card-hover p-8"
            >
              <h3 className="text-2xl font-bold text-dsr-text mb-6">Why Choose DevSecRoot?</h3>
              <div className="space-y-4">
                {features.map((feature) => (
                  <div key={feature.title} className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-black" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-dsr-text">{feature.title}</h4>
                      <p className="text-dsr-text/70 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Our Impact
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Numbers that reflect our commitment to excellence and client success
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-lg font-medium text-dsr-text mb-1">
                  {stat.label}
                </div>
                <div className="text-sm text-dsr-text/70">
                  {stat.description}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help secure and scale your applications
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Start Your Project
              </Link>
              <Link to="/case-studies" className="btn-secondary text-lg px-8 py-4">
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default AboutPage
